/**
 * @description API service for global settings management
 *
 * Key features:
 * - Get all settings for current company (grouped by category)
 * - Update company settings
 * - Manage role-level overrides
 *
 * ENTERPRISE ARCHITECTURE NOTES:
 * ==============================
 * 1. MULTI-COMPANY SUPPORT:
 *    - Settings are scoped to current company from CompanyContext
 *    - Multi-company merchants see settings for active company only
 *    - Company context passed via auth token
 *
 * 2. SETTING TIERS:
 *    - Merchant Settings (this service): Business operations configs
 *    - SO/Platform Settings (future): System configuration with guardrails
 *
 * 3. VALUE SOURCES:
 *    - 'default': Using platform default
 *    - 'company': Company has custom value
 *    - 'role': Role-level override exists (for specific roles)
 *
 * @dependencies
 * - apiClient: Axios instance with auth headers
 *
 * @see .claude/tasks/Global_Settings_Architecture.md
 */

import apiClient from './apiClient';

const settingsService = {
  /**
   * Get all settings for the current company, grouped by category
   *
   * Response shape:
   * {
   *   "time_clock": [
   *     {
   *       key: "time_clock.break_allowances",
   *       displayName: "Break Allowances",
   *       description: "...",
   *       schema: { type: "array", items: {...} },
   *       currentValue: [...],
   *       valueSource: "company" | "location" | "default" | "role",
   *       isLocked: false,
   *       canEdit: true,
   *       allowRoleOverride: true,
   *       allowLocationOverride: true,
   *       roleOverrides: [{ roleId, roleName, value }]
   *     }
   *   ],
   *   "invoicing": [...]
   * }
   *
   * @param {string} [locationId] - Optional location ID for location-scoped resolution
   * @returns {Promise<Object>} Settings grouped by category
   */
  getAllSettings: async (locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.get('/settings', { params });
    return response.data;
  },

  /**
   * Get available setting categories
   * @returns {Promise<{categories: string[]}>}
   */
  getCategories: async () => {
    const response = await apiClient.get('/settings/categories');
    return response.data;
  },

  /**
   * Get settings for a specific category
   * @param {string} category - Category name (e.g., 'time_clock')
   * @param {string} [locationId] - Optional location ID for location-scoped resolution
   * @returns {Promise<{category: string, settings: Array}>}
   */
  getByCategory: async (category, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.get(`/settings/category/${category}`, { params });
    return response.data;
  },

  /**
   * Get a single setting with resolved value
   * @param {string} key - Setting key (e.g., 'time_clock.break_allowances')
   * @param {Object} [options] - Optional resolution context
   * @param {number} [options.roleId] - Optional role ID for role-specific resolution
   * @param {string} [options.locationId] - Optional location ID for location-specific resolution
   * @returns {Promise<{key: string, value: any, source: string}>}
   */
  getSetting: async (key, options = {}) => {
    const { roleId = null, locationId = null } = options;
    const params = {};
    if (roleId) params.roleId = roleId;
    if (locationId) params.locationId = locationId;
    const response = await apiClient.get(`/settings/${encodeURIComponent(key)}`, { params });
    return response.data;
  },

  /**
   * Update a company or location setting
   * @param {string} key - Setting key
   * @param {any} value - New value
   * @param {string} [locationId] - Optional location ID for location-scoped update
   * @returns {Promise<{success: boolean, setting: Object}>}
   */
  updateSetting: async (key, value, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.put(`/settings/${encodeURIComponent(key)}`, { value }, { params });
    return response.data;
  },

  /**
   * Delete a company setting (reverts to default)
   * @param {string} key - Setting key
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deleteSetting: async (key) => {
    const response = await apiClient.delete(`/settings/${encodeURIComponent(key)}`);
    return response.data;
  },

  /**
   * Get all role overrides for the company
   * @returns {Promise<{roleOverrides: Array}>}
   */
  getAllRoleOverrides: async () => {
    const response = await apiClient.get('/settings/role-overrides');
    return response.data;
  },

  /**
   * Get overrides for a specific role
   * @param {number} roleId - Role ID
   * @returns {Promise<{roleId: number, overrides: Array}>}
   */
  getRoleOverrides: async (roleId) => {
    const response = await apiClient.get(`/settings/role-overrides/${roleId}`);
    return response.data;
  },

  /**
   * Set a role-level override
   * @param {number} roleId - Role ID
   * @param {string} key - Setting key
   * @param {any} value - Override value
   * @returns {Promise<{success: boolean, setting: Object}>}
   */
  setRoleOverride: async (roleId, key, value) => {
    const response = await apiClient.put(
      `/settings/role-overrides/${roleId}/${encodeURIComponent(key)}`,
      { value }
    );
    return response.data;
  },

  /**
   * Remove a role-level override (reverts to company setting)
   * @param {number} roleId - Role ID
   * @param {string} key - Setting key
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deleteRoleOverride: async (roleId, key) => {
    const response = await apiClient.delete(
      `/settings/role-overrides/${roleId}/${encodeURIComponent(key)}`
    );
    return response.data;
  },

  /**
   * Validate a value against a setting's schema (without saving)
   * @param {string} key - Setting key
   * @param {any} value - Value to validate
   * @returns {Promise<{valid: boolean, error?: string}>}
   */
  validateValue: async (key, value) => {
    const response = await apiClient.post('/settings/validate', { key, value });
    return response.data;
  }
};

export default settingsService;
